import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Collapse,
  Alert,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import { useAllBranches } from '../../../contexts/BranchesContext';

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

// ─── Reusable: branch multi-select / read-only chip list ───────────────────
export const BranchSelect = ({ branches, onChange, readOnly = false }) => {
  const { allBranches } = useAllBranches();

  if (readOnly) {
    return branches.length === 0 ? (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        אין סניפים מוגדרים
      </Typography>
    ) : (
      <Box display="flex" flexWrap="wrap" gap={0.5} py={0.5}>
        {branches.map((b) => (
          <Chip key={b} label={b} size="small" sx={{ bgcolor: '#e8f0fe', color: '#0054A5' }} />
        ))}
      </Box>
    );
  }

  return (
    <FormControl fullWidth size="small">
      <Select
        multiple
        displayEmpty
        value={branches}
        onChange={(e) => {
          const { value } = e.target;
          onChange(typeof value === 'string' ? value.split(',') : value);
        }}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <span style={{ color: '#dc2626' }}>לא נבחרו סניפים — לא יוצגו הזמנות</span>
          ) : (
            selected.join(', ')
          )
        }
      >
        {(allBranches || []).map((branch) => (
          <MenuItem key={branch.BRANCHNAME} value={branch.BRANCHNAME}>
            <Checkbox checked={branches.includes(branch.BRANCHNAME)} size="small" />
            <ListItemText primary={branch.BRANCHNAME} secondary={branch.BRANCHDES} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// ─── Head manager banner: unrestricted access, no branch config needed ─────
const HeadManagerBanner = () => (
  <Alert severity="success" icon={<StoreIcon fontSize="inherit" />}>
    מנהל ראשי — רואה את כל ההזמנות בסטטוס השהיית סחר, ללא הגבלת סניף.
  </Alert>
);

// ─── Manager view: edit own branches ────────────────────────────────────────
const ManagerBranchView = ({ execute }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [managerName, setManagerName] = useState('');
  const [isHeadManager, setIsHeadManager] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await execute('GET', `${BASE_URL}/api/tradeDelay/branches`);
      setBranches(res?.branches || []);
      setManagerName(res?.managerName || '');
      setIsHeadManager(!!res?.isHeadManager);
    } catch {
      setError('שגיאה בטעינת הסניפים');
    } finally {
      setLoading(false);
    }
  }, [execute]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const res = await execute('PUT', `${BASE_URL}/api/tradeDelay/branches`, { branches });
      setBranches(res?.branches || branches);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('שגיאה בשמירת הסניפים');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;

  if (isHeadManager) {
    return (
      <Box>
        <Typography variant="h6" color="#0054A5" mb={1.5}>
          {managerName}
        </Typography>
        <HeadManagerBanner />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6" color="#0054A5">
            הסניפים שלי
          </Typography>
          {managerName && (
            <Typography variant="body2" color="text.secondary">
              {managerName}
            </Typography>
          )}
        </Box>
        <Chip
          label={`${branches.length} סניפים`}
          size="small"
          sx={{ bgcolor: '#e8f0fe', color: '#0054A5' }}
        />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        רק הזמנות מהסניפים שנבחרו יוצגו עבורך ועבור מתאמי הסחר תחתיך. אם לא נבחר אף סניף, לא יוצגו הזמנות כלל.
        שימו לב: זו אותה רשימת סניפים כמו בעמוד ״הרשאות גישה״ — שינוי כאן משנה גם שם, ולהפך.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>הסניפים נשמרו בהצלחה</Alert>}

      <BranchSelect branches={branches} onChange={setBranches} />

      <Box mt={2}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#0054A5', '&:hover': { bgcolor: '#003d7a' } }}
        >
          שמור שינויים
        </Button>
      </Box>
    </Box>
  );
};

// ─── Employee (trade coordinator) view: read-only ───────────────────────────
const EmployeeBranchView = ({ execute }) => {
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [managerName, setManagerName] = useState('');
  const [isHeadManager, setIsHeadManager] = useState(false);

  useEffect(() => {
    execute('GET', `${BASE_URL}/api/tradeDelay/branches`)
      .then((res) => {
        setBranches(res?.branches || []);
        setManagerName(res?.managerName || '');
        setIsHeadManager(!!res?.isHeadManager);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [execute]);

  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;

  return (
    <Box>
      <Typography variant="h6" color="#0054A5" mb={0.5}>
        סניפי הצוות
      </Typography>
      {managerName && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          מנהל: {managerName}
        </Typography>
      )}
      {isHeadManager ? (
        <HeadManagerBanner />
      ) : (
        <>
          {branches.length === 0 && !managerName && (
            <Alert severity="info">לא שויכת למנהל עם רשימת סניפים מוגדרת</Alert>
          )}
          <BranchSelect branches={branches} onChange={() => {}} readOnly />
        </>
      )}
    </Box>
  );
};

// ─── Admin view: all managers ────────────────────────────────────────────────
const AdminManagerCard = ({ manager, execute, onSaved }) => {
  const isHeadManager = manager.tradeDelayRole === 'headManager';
  const [expanded, setExpanded] = useState(false);
  const [branches, setBranches] = useState(manager.Branches || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const res = await execute(
        'PUT',
        `${BASE_URL}/api/tradeDelay/managers/${manager._id}/branches`,
        { branches }
      );
      setBranches(res?.branches || branches);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved && onSaved(manager._id, res?.branches || branches);
    } catch {
      setError('שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f8ff' } }}
        onClick={() => setExpanded((p) => !p)}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography fontWeight={600} color="#1f2937">
            {manager.Name} {manager.LastName}
          </Typography>
          {isHeadManager ? (
            <Chip
              label="מנהל ראשי — רואה הכל"
              size="small"
              sx={{ bgcolor: '#f3e8fd', color: '#6a1b9a', fontWeight: 600 }}
            />
          ) : (
            <Chip
              label={`${branches.length} סניפים`}
              size="small"
              sx={{ bgcolor: branches.length ? '#e8f0fe' : '#fef2f2', color: branches.length ? '#0054A5' : '#dc2626' }}
            />
          )}
          {manager.Email && (
            <Typography variant="caption" color="text.secondary">
              {manager.Email}
            </Typography>
          )}
        </Box>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box px={2} py={2}>
          {isHeadManager ? (
            <HeadManagerBanner />
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
              {saved && <Alert severity="success" sx={{ mb: 1 }}>נשמר בהצלחה</Alert>}
              <BranchSelect branches={branches} onChange={setBranches} />
              <Box mt={1.5}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ bgcolor: '#0054A5', '&:hover': { bgcolor: '#003d7a' } }}
                >
                  שמור
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const AdminBranchView = ({ execute }) => {
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    execute('GET', `${BASE_URL}/api/tradeDelay/allManagersBranches`)
      .then((res) => setManagers(res?.managers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [execute]);

  const handleSaved = (managerId, newBranches) => {
    setManagers((prev) =>
      prev.map((m) => m._id === managerId ? { ...m, Branches: newBranches } : m)
    );
  };

  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;

  return (
    <Box>
      <Typography variant="h6" color="#0054A5" mb={0.5}>
        ניהול סניפים — כל המנהלים
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        הגדר רשימת סניפים לכל מנהל. ההזמנות יסוננו לפי רשימה זו עבורו ועבור העובדים תחתיו.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        מנהל ללא סניפים מוגדרים לא יראה אף הזמנה בטאב "הזמנות בהשהייה". זו אותה רשימת סניפים כמו בעמוד ״הרשאות גישה״.
      </Alert>

      {managers.length === 0 && (
        <Alert severity="warning">לא הוגדרו מנהלים במערכת ההשהיות</Alert>
      )}

      {managers.map((manager) => (
        <AdminManagerCard
          key={manager._id}
          manager={manager}
          execute={execute}
          onSaved={handleSaved}
        />
      ))}
    </Box>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────
const BranchManagementTab = ({ currentUser, execute }) => {
  const isAdmin = currentUser?.roles?.some((r) => r.admin);
  const isManager = ['manager', 'headManager'].includes(currentUser?.tradeDelayRole);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <StoreIcon sx={{ color: '#0054A5' }} />
        <Typography variant="h6" color="#0054A5">
          ניהול סניפים
        </Typography>
      </Box>

      {isAdmin ? (
        <AdminBranchView execute={execute} />
      ) : isManager ? (
        <ManagerBranchView execute={execute} />
      ) : (
        <EmployeeBranchView execute={execute} />
      )}
    </Box>
  );
};

export default BranchManagementTab;
